import React, { useState } from 'react';

const formatGender = (gender) =>
    ({ MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' }[gender] || gender);

const calculateAge = (dob) => {
    if (!dob) {
        return null;
    }

    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age -= 1;
    }

    return age;
};

const formatAddressLine = (parts) => parts.filter(Boolean).join(', ');

const QUICK_ACTIONS = [
    { icon: 'bi-envelope', label: 'Send Email', contactKey: 'email', prefKey: 'isEmail' },
    { icon: 'bi-chat-dots', label: 'Send SMS', contactKey: 'cell', prefKey: 'isSms' },
    { icon: 'bi-clipboard-data', label: 'Send Survey', contactKey: 'email', prefKey: 'isSurvey' },
    { icon: 'bi-file-text', label: 'Send Articles', contactKey: 'email', prefKey: 'isArticle' },
    { icon: 'bi-eyeglasses', label: 'Glass Pickup', prefKey: 'isGlass' },
    { icon: 'bi-eye', label: 'Lens Pickup', prefKey: 'isLens' },
    { icon: 'bi-heart', label: 'Thank You', contactKey: 'email', prefKey: 'isThankyou' },
    { icon: 'bi-emoji-smile', label: 'Welcome', contactKey: 'email', prefKey: 'isEmail', disableIfWelcomeSent: true },
];

const CONTACT_LABELS = {
    email: 'email',
    cell: 'cell number',
};

const getContactValue = (patient, contactKey) => {
    if (contactKey === 'email') {
        return patient.email?.trim();
    }

    if (contactKey === 'cell') {
        return patient.cellNumber?.trim();
    }

    return '';
};

const getQuickActionState = (action, patient, commPrefs) => {
    const reasons = [];

    if (action.contactKey && !getContactValue(patient, action.contactKey)) {
        reasons.push(`no ${CONTACT_LABELS[action.contactKey]} on file`);
    }

    if (action.prefKey && commPrefs != null && !commPrefs[action.prefKey]) {
        reasons.push('disabled in communication preferences');
    }

    if (action.disableIfWelcomeSent && patient.isWelcome) {
        reasons.push('welcome already sent');
    }

    return {
        disabled: reasons.length > 0,
        title: reasons.length > 0 ? `${action.label} — ${reasons.join('; ')}` : action.label,
    };
};

function PatientInfoCard({ patient, commPrefs }) {
    const [copiedField, setCopiedField] = useState(null);

    const handleCopy = async (field, value) => {
        if (!value) {
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }

        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const initials = `${patient.firstName?.charAt(0) || '?'}${patient.lastName?.charAt(0) || ''}`;
    const age = calculateAge(patient.dateOfBirth);
    const addressLine = formatAddressLine([
        patient.street,
        patient.city,
        patient.province,
        patient.postalCode,
        patient.country,
    ]);

    const quickActions = QUICK_ACTIONS;

    const contactItems = [
        {
            key: 'email',
            icon: 'bi-envelope',
            label: 'Email',
            value: patient.email,
        },
        {
            key: 'cell',
            icon: 'bi-phone',
            label: 'Cell',
            value: patient.cellNumber,
        },
        {
            key: 'phone',
            icon: 'bi-telephone',
            label: 'Home',
            value: patient.phoneNumber,
        },
    ];

    return (
        <div className="patient-detail-summary">
            <div className="patient-detail-summary__main">
                <div className="patient-detail-summary__identity">
                    <span className="patient-files-patient-cell__avatar patient-detail-summary__avatar">
                        {initials}
                    </span>

                    <div className="patient-detail-summary__identity-text">
                        <h2 className="patient-detail-summary__name">
                            {patient.firstName || 'N/A'} {patient.lastName || ''}
                            {patient.preferredName && (
                                <span className="patient-detail-summary__preferred">
                                    ({patient.preferredName})
                                </span>
                            )}
                        </h2>

                        <div className="patient-detail-summary__meta">
                            {patient.pmsId && (
                                <span className="patient-detail-meta-item">
                                    <i className="bi bi-person-badge" />
                                    PMS ID: {patient.pmsId}
                                </span>
                            )}

                            {patient.dateOfBirth && (
                                <span className="patient-detail-meta-item">
                                    <i className="bi bi-cake2" />
                                    {patient.dateOfBirth}
                                    {age != null && ` · ${age} yrs`}
                                </span>
                            )}

                            {patient.gender && (
                                <span className="patient-detail-meta-item">
                                    <i className="bi bi-gender-ambiguous" />
                                    {formatGender(patient.gender)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="patient-detail-summary__status">
                    <span
                        className={`clinic-settings-status-pill ${
                            patient.isActive ? 'is-open' : 'is-closed'
                        }`}
                    >
                        <i className={`bi ${patient.isActive ? 'bi-check-circle' : 'bi-x-circle'}`} />
                        {patient.isActive ? 'Active' : 'Inactive'}
                    </span>

                    <span
                        className={`clinic-settings-status-pill ${
                            patient.isWelcome ? 'is-active' : 'is-closed'
                        }`}
                    >
                        <i className={`bi ${patient.isWelcome ? 'bi-envelope-check' : 'bi-envelope'}`} />
                        {patient.isWelcome ? 'Welcome Sent' : 'Welcome Pending'}
                    </span>
                </div>
            </div>

            <div className="patient-detail-summary__grid">
                <div className="patient-detail-summary__contacts">
                    {contactItems.map((item) => (
                        <div key={item.key} className="patient-detail-contact-card">
                            <div className="patient-detail-contact-card__label">
                                <i className={`bi ${item.icon}`} />
                                {item.label}
                            </div>

                            {item.value ? (
                                <div className="patient-detail-contact-card__value">
                                    <span>{item.value}</span>
                                    <button
                                        type="button"
                                        className="patient-files-copy-btn"
                                        onClick={() => handleCopy(item.key, item.value)}
                                        title={`Copy ${item.label.toLowerCase()}`}
                                        aria-label={`Copy ${item.label.toLowerCase()}`}
                                    >
                                        <i
                                            className={`bi ${
                                                copiedField === item.key ? 'bi-check2 text-success' : 'bi-copy'
                                            }`}
                                        />
                                    </button>
                                </div>
                            ) : (
                                <span className="patient-detail-contact-card__empty">—</span>
                            )}
                        </div>
                    ))}
                </div>

                <div className="patient-detail-summary__address">
                    <div className="patient-detail-contact-card">
                        <div className="patient-detail-contact-card__label">
                            <i className="bi bi-geo-alt" />
                            Address
                        </div>
                        <div className="patient-detail-contact-card__value patient-detail-contact-card__value--stacked">
                            {addressLine || '—'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="patient-detail-summary__actions">
                {quickActions.map((action) => {
                    const { disabled, title } = getQuickActionState(action, patient, commPrefs);

                    return (
                        <button
                            key={action.label}
                            type="button"
                            className="patient-detail-action-btn"
                            disabled={disabled}
                            title={title}
                        >
                            <i className={`bi ${action.icon}`} />
                            <span>{action.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default PatientInfoCard;
